import React, { useMemo, useState } from 'react';
import { Button, Modal, ConfigProvider } from 'antd';
import { Product, ShoppingCartV2,
} from '../../../b2c/src/modules/ShoppingCart/ShoppingCartSliceV2';
import './baggage-management.less';
import { IoIosMan } from 'react-icons/io';
import {
  FaBaby,
  FaCheck,
  FaChild,
  FaPlane,
  FaPlus,
  FaSuitcase,
  FaSuitcaseRolling,
} from 'react-icons/fa';
import { MainProductType } from '../../../b2c/src/modules/ShoppingCart/interfaces';
import useIsPhoneWidth from '../../hooks/useIsPhoneWidth';

const getIcon = (guestType: string) => {
  switch (guestType) {
    case 'child':
      return <FaChild size={25} />;
    case 'infant':
      return <FaBaby size={25} />;
    default:
      return <IoIosMan size={32} />;
  }
};
const adjustGuestsArray = (
  guestsArray: any[],
  isBack: boolean,
  trollyPrice: number,
  suitcasePrice: number,
  relatedGuestIndexes: number[],
  flightSubProducts?: Product[],
  guestLabel = 'Guest'
) => {
  return guestsArray
    .filter((guest) => {
      return relatedGuestIndexes.includes(guest.g_index) && guest.type !== 'infant';
    })
    .map((g: any) => ({
      ...g,
      isBack,
      // plain-text name for the phone sheet (first_name becomes JSX below)
      displayName: g.first_name
        ? [g.first_name, g.last_name].filter(Boolean).join(' ')
        : `${guestLabel} ${g.g_index + 1}`,
      first_name: g.first_name ? (
        <>
          <span className="guest-type">
            {getIcon(g.type)} <div>-</div> {g.first_name}
          </span>
        </>
      ) : (
        <span className="guest-type">
          {getIcon(g.type)} <div>-</div> Guest {g.g_index + 1}
        </span>
      ),
      id: g.g_index,
      trollyPrice,
      suitcasePrice,
      hasSuitcase: flightSubProducts?.some(
        (subProduct: any) =>
          (subProduct.type === 'suitcase' ||
            subProduct.type_name === 'Suitcase') &&
          (subProduct.related_guest_indexes?.includes(g?.g_index) ||
            subProduct.related_guests_index?.includes(String(g.g_index)))
      ),
      hasTrolly: flightSubProducts?.some(
        (subProduct: any) =>
          (subProduct.type === MainProductType.Trolly ||
            subProduct.type_name === 'Trolly') &&
          (subProduct.related_guest_indexes?.includes(g?.g_index) ||
            subProduct.related_guests_index?.includes(String(g.g_index)))
      ),
    }));
};
interface Guest {
  id: string;
  displayName: string;
  first_name: string;
  last_name: string;
  type: string;
  hasSuitcase: boolean;
  hasTrolly: boolean;
  isBack: boolean;
  mainProduct: any;
  suitcasePrice: number;
  trollyPrice: number;
  g_index: string;
}

interface BaggageManagementProps {
  onChange: (...params: any[]) => void;
  /** prevCart - the cart when the panel opened (a saved order saves only what changed) */
  onSave: (prevCart?: ShoppingCartV2) => void;
  /** saving on the server - the save button waits */
  isSaving?: boolean;
  onClose?: (prevCart?: ShoppingCartV2) => void;
  mainProduct: Product;
  guests: any[];
  isManageBaggageOpen: boolean;
  setIsManageBaggageOpen: React.Dispatch<React.SetStateAction<boolean>>;
  shoppingCart?: ShoppingCartV2;
  isRoundTrip: boolean;
  flightFromBaggage: any;
  flightBackBaggage: any;
  t: Record<string, string>;
  direction:'ltr' | 'rtl'
}

const BaggageManagement: React.FC<BaggageManagementProps> = ({
  onChange,
  mainProduct,
  guests,
  shoppingCart,
  onClose,
  isManageBaggageOpen,
  setIsManageBaggageOpen,
  onSave,
  isSaving,
  isRoundTrip,
  flightFromBaggage,
  flightBackBaggage,
  t,
  direction
}) => {
  const isPhone = useIsPhoneWidth();
  const flightFromSuitcasePrice = flightFromBaggage?.suitcase_price;
  const flightFromTrollyPrice = flightFromBaggage?.trolly_price;
  const flightBackTrollyPrice = flightBackBaggage?.trolly_price;
  const flightBackSuitcasePrice = flightBackBaggage?.suitcase_price;
  const flightFromSuitcaseSaleAllowed = flightFromBaggage?.enable_sale_suitcase;
  const flightFromTrollySaleAllowed = flightFromBaggage?.enable_sale_trolly;
  const flightBackSuitcaseSaleAllowed = flightBackBaggage?.enable_sale_suitcase;
  const flightBackTrollySaleAllowed = flightBackBaggage?.enable_sale_trolly;

  const getSaleStatusMessage = (
    trollySaleAllowed: boolean,
    suitcaseSaleAllowed: boolean
  ) => {
    if (trollySaleAllowed && suitcaseSaleAllowed) return '';
    if (trollySaleAllowed && !suitcaseSaleAllowed)
      return (
        <div>
          <b className="sale-status"> {t.suitcase}</b>
          {` ${t.purchaseIsNotSupported}`}
        </div>
      );
    if (!trollySaleAllowed && suitcaseSaleAllowed)
      return (
        <div>
          <b className="sale-status"> {t.trolly}</b>
          {` ${t.purchaseIsNotSupported}`}
        </div>
      );
    if (!trollySaleAllowed && !suitcaseSaleAllowed)
      return (
        <div>
          <b className="sale-status">
            {t.suitcase} {t.and} {t.trolly}
          </b>
          {` ${t.purchaseIsNotSupported}`}
        </div>
      );
  };

  const saleStatusMessageFromFlight = getSaleStatusMessage(
    flightFromTrollySaleAllowed,
    flightFromSuitcaseSaleAllowed
  );
  const saleStatusMessageBackFlight = getSaleStatusMessage(
    flightBackTrollySaleAllowed,
    flightBackSuitcaseSaleAllowed
  );

  const memoisedShoppingCart = useMemo(() => {
    return shoppingCart;
  }, []);

  const flightFromSubProducts = mainProduct?.subProducts?.filter(
    (sb: { type: MainProductType; }) =>
      sb.type === MainProductType.Flight ||
      sb.type === MainProductType.oneWayFlight
  )?.[0]?.subProducts;
  const flightBackSubProducts = mainProduct?.subProducts?.filter(
    (sb: { type: MainProductType; }) =>
      sb.type === MainProductType.Flight ||
      sb.type === MainProductType.oneWayFlight
  )?.[1]?.subProducts;

  const filteredGuest = adjustGuestsArray(
    guests,
    false,
    flightFromTrollyPrice,
    flightFromSuitcasePrice,
    mainProduct.related_guest_indexes,
    flightFromSubProducts,
    t.guest
  );

  const filteredGuestBaggageBack = adjustGuestsArray(
    guests,
    true,
    flightBackTrollyPrice,
    flightBackSuitcasePrice,
    mainProduct.related_guest_indexes,
    flightBackSubProducts,
    t.guest
  );

  const [guestBaggage, setGuestBaggage] = useState<Guest[]>(filteredGuest);
  const [guestBaggageBack, setGuestBaggageBack] = useState<Guest[]>(
    filteredGuestBaggageBack
  );

  const handleCheckboxChange = (
    guestId: string,
    baggageType: 'hasSuitcase' | 'hasTrolly',
    isBack: boolean = false
  ) => {
    const setGuestBaggageState = isBack ? setGuestBaggageBack : setGuestBaggage;
    setGuestBaggageState((prevState) =>
      prevState.map((guest: Guest) =>
        guest.id === guestId
          ? { ...guest, [baggageType]: !guest[baggageType] }
          : guest
      )
    );
  };

  const handleCloseModal = () => {
    setIsManageBaggageOpen(false);
    onClose && onClose(memoisedShoppingCart);
  };

  const productDataFlights =
    mainProduct.product_data.flightsInfo ?? mainProduct.product_data.flights;

  // ── a bottom sheet on phones (above the cart sheet), a centered dialog elsewhere ──
  //   Manage baggage                          ✕
  //   ┌ ✈ Tel Aviv → Paris ──────────────────┐   one card per flight
  //   │ 👤 Tazo Nebieridze                    │
  //   │ ┌ 🧳 Suitcase  40$ ✓ ┐┌ 🛄 Trolley 25$ + ┐  toggle tiles
  //   └──────────────────────────────────────┘
  //   [               Save                  ]     pinned footer
  // Same state and onChange calls as the desktop checkboxes.
  const toggleBaggage = (
    guest: Guest,
    type: 'suitcase' | 'trolly',
    price: number,
    isBack: boolean
  ) => {
    const key = type === 'suitcase' ? 'hasSuitcase' : 'hasTrolly';
    handleCheckboxChange(guest.id, key, isBack);
    onChange(guest[key] ? 0 : 1, isBack ? 1 : 0, type, price, guest.id);
  };

  const legFrom = productDataFlights.flightFrom[0].from.name;
  const legTo =
    productDataFlights.flightFrom[productDataFlights.flightFrom.length - 1].to
      .name;

  const renderPhoneLeg = ({
    routes,
    notes,
    guestList,
    isBack,
    suitcasePrice,
    trollyPrice,
    suitcaseAllowed,
    trollyAllowed,
  }: {
    routes: string[];
    notes: React.ReactNode[];
    guestList: Guest[];
    isBack: boolean;
    suitcasePrice: number;
    trollyPrice: number;
    suitcaseAllowed: boolean;
    trollyAllowed: boolean;
  }) => {
    const bagTypes = [
      suitcaseAllowed && {
        type: 'suitcase' as const,
        key: 'hasSuitcase' as const,
        label: t.suitcase,
        price: suitcasePrice,
        icon: <FaSuitcase />,
      },
      trollyAllowed && {
        type: 'trolly' as const,
        key: 'hasTrolly' as const,
        label: t.trolly,
        price: trollyPrice,
        icon: <FaSuitcaseRolling />,
      },
    ].filter(Boolean) as Array<{
      type: 'suitcase' | 'trolly';
      key: 'hasSuitcase' | 'hasTrolly';
      label: string;
      price: number;
      icon: React.ReactNode;
    }>;

    return (
      <section className="bm-leg">
        <div className="bm-leg__head">
          <span className="bm-leg__icon">
            <FaPlane />
          </span>
          <span className="bm-leg__routes">
            {routes.map((route) => (
              <span key={route} className="bm-leg__route">
                {route}
              </span>
            ))}
          </span>
        </div>
        {notes.filter(Boolean).map((note, i) => (
          <div key={i} className="bm-leg__note">
            {note}
          </div>
        ))}
        {/* one tap for every traveler: adds the bag to whoever lacks it, or - when all have it - removes it */}
        {bagTypes.length > 0 && guestList.length > 1 && (
          <div className="bm-all">
            <span className="bm-all__label">{t.allTravelers}</span>
            <div className="bm-all__actions">
              {bagTypes.map((bag) => {
                const allOn = guestList.every((guest) => !!guest[bag.key]);
                return (
                  <button
                    key={bag.type}
                    type="button"
                    className={`bm-all__btn${allOn ? ' is-on' : ''}`}
                    aria-pressed={allOn}
                    onClick={() =>
                      guestList
                        .filter((guest) => !!guest[bag.key] === allOn)
                        .forEach((guest) =>
                          toggleBaggage(guest, bag.type, bag.price, isBack)
                        )
                    }
                  >
                    {allOn ? <FaCheck /> : <FaPlus />}
                    <span>{bag.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {bagTypes.length > 0 &&
          guestList.map((guest) => (
            <div key={guest.id} className="bm-guest">
              <div className="bm-guest__name">
                <span className="bm-guest__avatar">{getIcon(guest.type)}</span>
                <span className="bm-guest__label">{guest.displayName}</span>
              </div>
              <div className="bm-guest__bags">
                {bagTypes.map((bag) => {
                  const isOn = !!guest[bag.key];
                  return (
                    <button
                      key={bag.type}
                      type="button"
                      className={`bm-bag${isOn ? ' is-on' : ''}`}
                      aria-pressed={isOn}
                      onClick={() =>
                        toggleBaggage(guest, bag.type, bag.price, isBack)
                      }
                    >
                      <span className="bm-bag__icon">{bag.icon}</span>
                      <span className="bm-bag__text">
                        <span className="bm-bag__name">{bag.label}</span>
                        <span className="bm-bag__price">
                          {bag.price?.toFixed(0)}$
                        </span>
                      </span>
                      <span className="bm-bag__check">
                        {isOn ? <FaCheck /> : <FaPlus />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
      </section>
    );
  };

  return (
    <ConfigProvider direction={direction}>
      <Modal
        open={!!isManageBaggageOpen}
        onCancel={handleCloseModal}
        title={t.manageBaggage}
        // phones: a bottom sheet above the cart sheet; elsewhere a centered dialog - the same cards
        wrapClassName={isPhone ? 'm-sheet m-sheet--grabber baggage-sheet' : 'baggage-dialog'}
        width={isPhone ? undefined : 560}
        className="baggage-management-modal"
        centered
        footer={
          <Button
            type="primary"
            block
            size="large"
            onClick={() => onSave(memoisedShoppingCart)}
            loading={isSaving}
            className="save-btn btn-style-5"
          >
            {t.save}
          </Button>
        }
      >
        <div id="baggage-management" className={`bm-body${isPhone ? ' is-phone' : ''}`}>
          {renderPhoneLeg({
            routes: [
              `${legFrom} → ${legTo}`,
              ...(isRoundTrip ? [`${legTo} → ${legFrom}`] : []),
            ],
            notes: [
              saleStatusMessageFromFlight,
              isRoundTrip ? t.baggageIsForBothFlights : null,
            ],
            guestList: guestBaggage,
            isBack: false,
            suitcasePrice: flightFromSuitcasePrice,
            trollyPrice: flightFromTrollyPrice,
            suitcaseAllowed: !!flightFromSuitcaseSaleAllowed,
            trollyAllowed: !!flightFromTrollySaleAllowed,
          })}
          {flightBackSubProducts &&
            !isRoundTrip &&
            renderPhoneLeg({
              routes: [`${legTo} → ${legFrom}`],
              notes: [saleStatusMessageBackFlight],
              guestList: guestBaggageBack,
              isBack: true,
              suitcasePrice: flightBackSuitcasePrice,
              trollyPrice: flightBackTrollyPrice,
              suitcaseAllowed: !!flightBackSuitcaseSaleAllowed,
              trollyAllowed: !!flightBackTrollySaleAllowed,
            })}
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default BaggageManagement;
